"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../../lib/utils'
import { WidgetBase, WidgetBaseProps } from '../core/widget-base'
import { 
  AlertTriangle,
  CheckCircle,
  Info,
  TrendingUp,
  TrendingDown,
  Zap,
  Target,
  Activity
} from 'lucide-react'
import { Badge } from '../../ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip'
import { useSubscription } from '../../../hooks/use-subscription'
import { ProLockScreen } from '../../shared/pro-lock-screen'

export interface GaugeData {
  value: number
  min?: number
  max?: number
  label: string
  unit?: string
  target?: number
  thresholds?: Array<{
    value: number
    label?: string
    color: string
  }>
  status?: 'success' | 'warning' | 'danger' | 'info' | 'neutral'
  trend?: {
    value: number
    type: 'increase' | 'decrease' | 'neutral'
  }
  segments?: Array<{
    label: string
    value: number
    color?: string
  }>
  description?: string
}

export interface GaugeWidgetProps extends Omit<Partial<WidgetBaseProps>, 'variant'> {
  data: GaugeData | GaugeData[]
  title?: string
  variant?: 'radial' | 'semicircle' | 'linear' | 'speedometer' | 'progress'
  backgroundVariant?: 'default' | 'minimal' | 'glass' | 'bordered' | 'gradient' | 'colored' | 'overlay' | 'floating'
  gradientDirection?: 'to-t' | 'to-tr' | 'to-r' | 'to-br' | 'to-b' | 'to-bl' | 'to-l' | 'to-tl'
  showValue?: boolean
  showTarget?: boolean
  showThresholds?: boolean
  showLabels?: boolean
  showTrend?: boolean
  showPercentage?: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl'
  animate?: boolean

  strokeWidth?: number
  onGaugeClick?: (gauge: GaugeData) => void
}

export function GaugeWidget({
  data,
  title,
  variant = 'radial',
  backgroundVariant,
  gradientDirection,
  showValue = true,
  showTarget = true,
  showThresholds = true,
  showLabels = true,
  showTrend = true,
  showPercentage = true,
  size = 'md',
  animate = true,

  strokeWidth = 12,
  onGaugeClick,
  ...widgetProps
}: GaugeWidgetProps) {
  const { hasProAccess, isLoading } = useSubscription();

  const gauges = Array.isArray(data) ? data : [data]
  
  // Size configurations
  const sizeConfig = {
    sm: { width: 120, height: 120, fontSize: 'text-lg', strokeWidth: 8 },
    md: { width: 180, height: 180, fontSize: 'text-2xl', strokeWidth: 12 },
    lg: { width: 240, height: 240, fontSize: 'text-3xl', strokeWidth: 16 },
    xl: { width: 320, height: 320, fontSize: 'text-4xl', strokeWidth: 20 }
  }

  const config = sizeConfig[size]
  
  // Format number with max 2 decimal places
  const formatNumber = (value: number): string => {
    if (value % 1 !== 0) {
      return (Math.round(value * 100) / 100).toString()
    }
    return value.toString()
  }
  
  // Calculate percentage
  const getPercentage = (gauge: GaugeData) => {
    const min = gauge.min ?? 0
    const max = gauge.max ?? 100
    const range = max - min
    return ((gauge.value - min) / range) * 100
  }

  // Get color based on thresholds or status
  const getColor = (gauge: GaugeData, percentage: number) => {
    if (gauge.thresholds && gauge.thresholds.length > 0) {
      // Find the appropriate threshold
      const sortedThresholds = [...gauge.thresholds].sort((a, b) => a.value - b.value)
      for (let i = sortedThresholds.length - 1; i >= 0; i--) {
        if (percentage >= sortedThresholds[i].value) {
          return sortedThresholds[i].color
        }
      }
      return sortedThresholds[0].color
    }

    // Default color based on status
    const statusColors = {
      success: '#22c55e',
      warning: '#f59e0b',
      danger: '#ef4444',
      info: '#3b82f6',
      neutral: '#6b7280'
    }

    if (gauge.status) {
      return statusColors[gauge.status]
    }

    // Default gradient based on percentage
    if (percentage >= 75) return '#22c55e'
    if (percentage >= 50) return '#f59e0b'
    if (percentage >= 25) return '#fb923c'
    return '#ef4444'
  }

  // Render radial gauge
  const renderRadialGauge = (gauge: GaugeData, index: number) => {
    const percentage = getPercentage(gauge)
    const radius = (config.width - config.strokeWidth) / 2
    const circumference = 2 * Math.PI * radius
    const strokeDashoffset = circumference - (percentage / 100) * circumference
    const color = getColor(gauge, percentage)
    const targetAngle = gauge.target ? ((gauge.target - (gauge.min ?? 0)) / ((gauge.max ?? 100) - (gauge.min ?? 0))) * 360 : null

    return (
      <motion.div
        key={index}
        className="relative flex flex-col items-center cursor-pointer"
        onClick={() => onGaugeClick?.(gauge)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <svg
          width={config.width}
          height={config.height}
          className="transform -rotate-90"
        >
          {/* Background circle */}
          <circle
            cx={config.width / 2}
            cy={config.height / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={config.strokeWidth}
            className="text-muted/20"
          />
          
          {/* Thresholds */}
          {showThresholds && gauge.thresholds && gauge.thresholds.map((threshold, idx) => {
            const thresholdAngle = ((threshold.value / 100) * 360 - 90) * (Math.PI / 180)
            const x1 = config.width / 2 + (radius - config.strokeWidth) * Math.cos(thresholdAngle)
            const y1 = config.height / 2 + (radius - config.strokeWidth) * Math.sin(thresholdAngle)
            const x2 = config.width / 2 + (radius + config.strokeWidth) * Math.cos(thresholdAngle)
            const y2 = config.height / 2 + (radius + config.strokeWidth) * Math.sin(thresholdAngle)
            
            return (
              <line
                key={idx}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={threshold.color}
                strokeWidth={2}
                opacity={0.5}
              />
            )
          })}

          {/* Progress circle */}
          <motion.circle
            cx={config.width / 2}
            cy={config.height / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={config.strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset }}
            transition={{ duration: animate ? 1 : 0, ease: "easeOut" }}
          />

          {/* Target indicator */}
          {showTarget && targetAngle !== null && (
            <g transform={`rotate(${targetAngle} ${config.width / 2} ${config.height / 2})`}>
              <line
                x1={config.width / 2}
                y1={config.strokeWidth}
                x2={config.width / 2}
                y2={config.strokeWidth + 10}
                stroke="currentColor"
                strokeWidth={3}
                className="text-primary"
              />
            </g>
          )}
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center transform rotate-0">
          {showValue && (
            <motion.div
              className={cn(config.fontSize, "font-bold")}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              {formatNumber(gauge.value)}
              {gauge.unit && <span className="text-sm ml-1">{gauge.unit}</span>}
            </motion.div>
          )}
          
          {showPercentage && (
            <div className="text-sm text-muted-foreground">
              {formatNumber(percentage)}%
            </div>
          )}
          
          {showLabels && (
            <div className="text-xs text-muted-foreground mt-1 text-center px-2">
              {gauge.label}
            </div>
          )}

          {showTrend && gauge.trend && (
            <div className={cn(
              "flex items-center gap-1 mt-2",
              gauge.trend.type === 'increase' ? 'text-green-600' : 
              gauge.trend.type === 'decrease' ? 'text-red-600' : 
              'text-gray-600'
            )}>
              {gauge.trend.type === 'increase' ? <TrendingUp className="h-3 w-3" /> : 
               gauge.trend.type === 'decrease' ? <TrendingDown className="h-3 w-3" /> : null}
              <span className="text-xs font-medium">{formatNumber(gauge.trend.value)}%</span>
            </div>
          )}
        </div>
      </motion.div>
    )
  }

  // Render linear gauge
  const renderLinearGauge = (gauge: GaugeData, index: number) => {
    const percentage = getPercentage(gauge)
    const color = getColor(gauge, percentage)

    return (
      <motion.div
        key={index}
        className="w-full space-y-2 cursor-pointer"
        onClick={() => onGaugeClick?.(gauge)}
        whileHover={{ scale: 1.01 }}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            {showLabels && (
              <span className="text-sm font-medium">{gauge.label}</span>
            )}
            {gauge.status && (
              <Badge variant="outline" className="text-xs">
                {gauge.status}
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {showValue && (
              <span className={cn("font-bold", size === 'sm' ? 'text-base' : 'text-lg')}>
                {formatNumber(gauge.value)}
                {gauge.unit && <span className="text-sm ml-1 text-muted-foreground">{gauge.unit}</span>}
              </span>
            )}
            {showPercentage && (
              <span className="text-sm text-muted-foreground">
                ({formatNumber(percentage)}%)
              </span>
            )}
          </div>
        </div>

        <div className="relative">
          {/* Background bar */}
          <div className={cn(
            "w-full rounded-full bg-muted/30 overflow-hidden",
            size === 'sm' ? 'h-2' : size === 'md' ? 'h-3' : size === 'lg' ? 'h-4' : 'h-5'
          )}>
            {/* Progress bar */}
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: color }}
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{ duration: animate ? 1 : 0, ease: "easeOut" }}
            />
          </div>

          {/* Thresholds markers */}
          {showThresholds && gauge.thresholds && gauge.thresholds.map((threshold, idx) => (
            <TooltipProvider key={idx}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-0.5 h-full bg-background"
                    style={{ left: `${threshold.value}%` }}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{threshold.label || `${threshold.value}%`}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}

          {/* Target marker */}
          {showTarget && gauge.target && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="absolute -top-1 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-primary"
                    style={{ 
                      left: `${((gauge.target - (gauge.min ?? 0)) / ((gauge.max ?? 100) - (gauge.min ?? 0))) * 100}%`,
                      transform: 'translateX(-50%)'
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Target: {formatNumber(gauge.target)}{gauge.unit}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Min/Max labels */}
        {showLabels && (
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{gauge.min ?? 0}</span>
            <span>{gauge.max ?? 100}</span>
          </div>
        )}
      </motion.div>
    )
  }

  // Render gauge based on variant
  const renderGauge = (gauge: GaugeData, index: number) => {
    switch (variant) {
      case 'linear':
      case 'progress':
        return renderLinearGauge(gauge, index)
      case 'radial':
      default:
        return renderRadialGauge(gauge, index)
    }
  }

  // Pro access check - moved to the end after all hooks
  if (!isLoading && !hasProAccess) {
    return (
      <ProLockScreen 
        componentName="Gauge Widget" 
        compact={true}
      />
    );
  }

  return (
    <WidgetBase
      title={title}
      variant={backgroundVariant}
      gradientDirection={gradientDirection}
      {...widgetProps}
    >
      <div className={cn(
        "flex flex-wrap items-center justify-center",
        variant === 'linear' || variant === 'progress' ? 'flex-col space-y-4' : 'gap-6',
        "p-4"
      )}>
        {gauges.map((gauge, index) => renderGauge(gauge, index))}
      </div>
    </WidgetBase>
  )
}

export default GaugeWidget